import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { LogIn, ArrowRight } from 'lucide-react';
import axios from 'axios';

const LoginPage = ({ onLogin }) => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPass, setShowPass] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [forgotMode, setForgotMode] = useState(false);
    const [forgotEmail, setForgotEmail] = useState('');
    const [forgotSent, setForgotSent] = useState(false);
    const [jobCount, setJobCount] = useState(null);
    const navigate = useNavigate();
    const location = useLocation();
    const justRegistered = location.state?.registered === true;

    useEffect(() => {
        axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/jobs/count`)
            .then(r => setJobCount(r.data))
            .catch(() => {});
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (!username || !password) {
            setError('Username and password are required.');
            return;
        }
        setBusy(true);
        try {
            const res = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/auth/login`, { username, password });
            if (res.data?.access_token && res.data?.refresh_token) {
                onLogin(res.data.access_token, res.data.refresh_token, username, rememberMe);
                // Return to the page the guard redirected from, if any.
                navigate(location.state?.from?.pathname || '/', { replace: true });
            } else {
                setError('Login failed. Please check your credentials.');
            }
        } catch {
            setError('Incorrect username or password.');
        } finally {
            setBusy(false);
        }
    };

    const handleForgot = async (e) => {
        e.preventDefault();
        if (!forgotEmail) return;
        setBusy(true);
        try {
            await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/auth/forgot-password`, { email: forgotEmail });
        } catch { /* show success regardless to avoid email enumeration */ }
        setForgotSent(true);
        setBusy(false);
    };

    return (
        <div className="lg-page">
            <div className="lg-shell">

                {/* ── Left: form column ── */}
                <div className="lg-form-col">
                    <div className="lg-form-wrap">

                        {!forgotMode ? (
                            <>
                                <header className="lg-form-head">
                                    <span className="eyebrow">Welcome back</span>
                                    <h1 className="lg-title">Log in to <span className="lg-serif">Interim.</span></h1>
                                    <p className="lg-sub">Get back to your jobs, candidates, and applications.</p>
                                </header>

                                <form className="lg-form" onSubmit={handleSubmit}>
                                    <label className="lg-field">
                                        <span className="lg-label">Username or email</span>
                                        <input
                                            type="text"
                                            autoComplete="username"
                                            className="lg-input"
                                            placeholder="e.g. askar.k or you@example.com"
                                            value={username}
                                            onChange={e => setUsername(e.target.value)}
                                            autoFocus
                                        />
                                    </label>

                                    <label className="lg-field">
                                        <span className="lg-field-row">
                                            <span className="lg-label">Password</span>
                                            <button type="button" className="lg-link-tiny" onClick={() => { setForgotMode(true); setError(''); }}>
                                                Forgot?
                                            </button>
                                        </span>
                                        <span className="lg-password">
                                            <input
                                                type={showPass ? 'text' : 'password'}
                                                autoComplete="current-password"
                                                className="lg-input"
                                                placeholder="••••••••"
                                                value={password}
                                                onChange={e => setPassword(e.target.value)}
                                            />
                                            <button type="button" className="lg-show" onClick={() => setShowPass(s => !s)}>
                                                {showPass ? 'Hide' : 'Show'}
                                            </button>
                                        </span>
                                    </label>

                                    <label className="lg-check">
                                        <input
                                            type="checkbox"
                                            checked={rememberMe}
                                            onChange={e => setRememberMe(e.target.checked)}
                                        />
                                        <span>Remember me on this device</span>
                                    </label>

                                    {justRegistered && (
                                        <div className="lg-success">
                                            Account created! Log in to get started.
                                        </div>
                                    )}
                                    {error && <div className="lg-error">{error}</div>}

                                    <button type="submit" className="ix-btn ix-btn-primary ix-btn-lg lg-submit" disabled={busy}>
                                        {busy ? 'Logging in…' : <><LogIn size={14} /> Log in</>}
                                    </button>

                                    <div className="lg-divider"><span>or continue with</span></div>

                                    <button
                                        type="button"
                                        className="lg-sso-btn"
                                        onClick={() => setError('Google login is coming soon.')}
                                    >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                            <path d="M21.6 12.23c0-.72-.06-1.4-.18-2.05H12v3.88h5.4a4.62 4.62 0 0 1-2 3.03v2.52h3.23c1.89-1.74 2.97-4.3 2.97-7.38Z" fill="#4285F4"/>
                                            <path d="M12 22c2.7 0 4.96-.9 6.63-2.43l-3.24-2.52c-.9.6-2.05.96-3.39.96-2.6 0-4.81-1.76-5.6-4.12H3.05v2.6A10 10 0 0 0 12 22Z" fill="#34A853"/>
                                            <path d="M6.4 13.89A6.01 6.01 0 0 1 6.07 12c0-.66.11-1.3.33-1.89V7.51H3.05A10 10 0 0 0 2 12c0 1.61.39 3.14 1.05 4.49l3.35-2.6Z" fill="#FBBC05"/>
                                            <path d="M12 5.98c1.47 0 2.79.5 3.83 1.5l2.87-2.87C16.95 2.99 14.7 2 12 2A10 10 0 0 0 3.05 7.51l3.35 2.6C7.19 7.74 9.4 5.98 12 5.98Z" fill="#EA4335"/>
                                        </svg>
                                        Continue with Google
                                    </button>
                                </form>

                                <div className="lg-foot">
                                    <span>New to Interim?</span>
                                    <Link to="/register" className="lg-link">
                                        Create an account <ArrowRight size={12} />
                                    </Link>
                                </div>
                            </>
                        ) : (
                            <>
                                <header className="lg-form-head">
                                    <span className="eyebrow">Password reset</span>
                                    <h1 className="lg-title">Forgot your <span className="lg-serif">password?</span></h1>
                                    <p className="lg-sub">Enter your email and we'll send a reset link if it's registered.</p>
                                </header>

                                {!forgotSent ? (
                                    <form className="lg-form" onSubmit={handleForgot}>
                                        <label className="lg-field">
                                            <span className="lg-label">Email address</span>
                                            <input
                                                type="email"
                                                autoComplete="email"
                                                className="lg-input"
                                                placeholder="you@example.com"
                                                value={forgotEmail}
                                                onChange={e => setForgotEmail(e.target.value)}
                                                autoFocus
                                            />
                                        </label>
                                        <button
                                            type="submit"
                                            className="ix-btn ix-btn-primary ix-btn-lg lg-submit"
                                            disabled={busy || !forgotEmail}
                                        >
                                            {busy ? 'Sending…' : 'Send reset link'}
                                        </button>
                                        <button type="button" className="lg-back-btn" onClick={() => { setForgotMode(false); setForgotEmail(''); }}>
                                            ← Back to log in
                                        </button>
                                    </form>
                                ) : (
                                    <div className="lg-form">
                                        <div className="lg-success">
                                            If <strong>{forgotEmail}</strong> is registered, a reset link is on its way. Check your inbox.
                                        </div>
                                        <button type="button" className="lg-back-btn" onClick={() => { setForgotMode(false); setForgotEmail(''); setForgotSent(false); }}>
                                            ← Back to log in
                                        </button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    <footer className="lg-legal mono">
                        © 2026 Interim &nbsp;·&nbsp;
                        <Link to="/about" style={{ color: 'inherit' }}>Terms</Link>
                        &nbsp;·&nbsp;
                        <Link to="/about" style={{ color: 'inherit' }}>Privacy</Link>
                    </footer>
                </div>

                {/* ── Right: aside panel ── */}
                <aside className="lg-aside">
                    <div className="lg-aside-bg" />
                    <div className="lg-aside-inner">

                        <div className="lg-aside-pulse">
                            <span className="lg-dot-pulse" />
                            {jobCount !== null ? `${jobCount.toLocaleString()} job${jobCount !== 1 ? 's' : ''} open right now` : 'Jobs open right now'}
                        </div>

                        <h2 className="lg-aside-title">
                            One day.<br />One job. <span className="lg-aside-serif">Done.</span>
                        </h2>
                        <p className="lg-aside-body">
                            The marketplace for single-day work. Post a job in minutes — or pick one up before lunch.
                        </p>

                        <div className="lg-aside-card">
                            <div className="lg-aside-card-meta">
                                <span className="ml-badge lg-badge-accepted">
                                    <span className="ml-badge-dot" />
                                    Accepted
                                </span>
                                <span>Sat 26 Apr · 14:00</span>
                            </div>
                            <div className="lg-aside-card-title">Weekend bar shift, busy gastropub</div>
                            <div className="lg-aside-card-foot">
                                <div className="lg-aside-card-poster">
                                    <span className="lg-aside-avatar">OS</span>
                                    <div>
                                        <div className="lg-aside-card-name">The Old Standard</div>
                                        <div>★ 4.9 · Shoreditch, E1</div>
                                    </div>
                                </div>
                                <div className="lg-aside-card-pay">£112</div>
                            </div>
                        </div>

                        <div className="lg-aside-card lg-aside-card-2">
                            <div className="lg-aside-card-meta">
                                <span className="lg-urgent-tag">⚡ Today</span>
                                <span>16:00 – 17:00</span>
                            </div>
                            <div className="lg-aside-card-title">Help moving a sofa, 2 floors up</div>
                            <div className="lg-aside-card-foot">
                                <div className="lg-aside-card-poster">
                                    <span className="lg-aside-avatar">ML</span>
                                    <div>
                                        <div className="lg-aside-card-name">Marcus L.</div>
                                        <div>★ 4.8 · Hackney, E8</div>
                                    </div>
                                </div>
                                <div className="lg-aside-card-pay">£45</div>
                            </div>
                        </div>

                        <div className="lg-aside-stats">
                            <div>
                                <div className="lg-aside-stat-n">11 min</div>
                                <div>avg time to first reply</div>
                            </div>
                            <div className="lg-aside-stats-div" />
                            <div>
                                <div className="lg-aside-stat-n">£780k</div>
                                <div>paid out this month</div>
                            </div>
                        </div>

                    </div>
                </aside>
            </div>
        </div>
    );
};

export default LoginPage;
